import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import { blinkService } from '../services/blinkService';
import BlinkLoginModal from '../components/BlinkLoginModal';
import { format } from 'date-fns';

const { FiVideo, FiShield, FiBattery, FiWifi, FiPlay, FiRefreshCw, FiThermometer, FiLogOut } = FiIcons;

export default function BlinkCameras() {
  const { state, dispatch } = useFamilyContext();
  const [auth, setAuth] = useState(null); // { authtoken, region, accountId, clientId }
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [thumbnails, setThumbnails] = useState({}); // { cameraId: blobUrl }
  const [refreshing, setRefreshing] = useState(false);

  // Load auth from localStorage or DB on mount
  useEffect(() => {
    const loadAuth = async () => {
        // Try local storage first for session persistence
        const localAuth = localStorage.getItem('blink_auth');
        if (localAuth) {
            setAuth(JSON.parse(localAuth));
        } else if (isSupabaseConfigured()) {
            // Try DB
            const { data } = await supabase.from('app_settings').select('value').eq('key', 'blink_auth').single();
            if (data?.value) {
                setAuth(data.value);
            }
        }
    };
    loadAuth();
  }, []);

  // Fetch dashboard when auth changes
  useEffect(() => {
    if (auth) {
        fetchDashboard();
    }
  }, [auth]);

  const handleLoginSuccess = async (authData) => {
    setAuth(authData);
    // Persist
    localStorage.setItem('blink_auth', JSON.stringify(authData));
    if (isSupabaseConfigured()) {
        await supabase.from('app_settings').upsert({ key: 'blink_auth', value: authData, updated_at: new Date() });
    }
  };

  const handleLogout = async () => {
      setAuth(null);
      setDashboard(null);
      localStorage.removeItem('blink_auth');
      if (isSupabaseConfigured()) {
          // Optional: Remove from DB or keep for other devices? Let's keep DB sync simple for now.
          // await supabase.from('app_settings').delete().eq('key', 'blink_auth');
      }
  };

  const fetchDashboard = async () => {
      if (!auth) return;
      setRefreshing(true);
      try {
          const data = await blinkService.getDashboard(auth);
          setDashboard(data);
          
          // Load thumbnails for new cameras
          if (data.cameras) {
              data.cameras.forEach(cam => {
                  if (cam.thumbnail && !thumbnails[cam.id]) {
                      loadThumbnail(cam.id, cam.thumbnail);
                  }
              });
          }
      } catch (err) {
          console.error("Dashboard fetch error:", err);
          if (err.message.includes('Unauthorized') || err.message.includes('401')) {
              alert("Sitzung abgelaufen. Bitte neu einloggen.");
              handleLogout();
          }
      } finally {
          setRefreshing(false);
      }
  };

  const loadThumbnail = async (camId, url) => {
      try {
          const blobUrl = await blinkService.getThumbnail(auth, url);
          setThumbnails(prev => ({ ...prev, [camId]: blobUrl }));
      } catch (e) {
          console.warn(`Failed to load thumbnail for ${camId}`, e);
      }
  };

  const toggleArm = async (networkId, currentStatus) => {
      if (!auth) return;
      const newState = currentStatus === 'armed' ? 'disarm' : 'arm';
      
      // Optimistic Update
      setDashboard(prev => ({
          ...prev,
          network: {
              ...prev.network,
              [networkId]: { ...prev.network[networkId], armed: newState === 'arm' }
          }
      }));

      try {
          await blinkService.setArmState(auth, networkId, newState);
          // Refresh to confirm
          setTimeout(fetchDashboard, 2000);
      } catch (e) {
          alert('Fehler beim Schalten: ' + e.message);
          fetchDashboard(); // Revert
      }
  };

  // Helper to find network status
  const getNetworkStatus = (networkId) => {
      // The dashboard structure from Blink varies slightly by version.
      // Usually data.network is an object or array.
      // We assume simple structure for now based on common API responses.
      if (dashboard?.network && dashboard.network[networkId]) {
          return dashboard.network[networkId].armed;
      }
      // Fallback search in array
      if (Array.isArray(dashboard?.networks)) {
          const net = dashboard.networks.find(n => n.id === networkId);
          return net ? net.armed : false;
      }
      return false;
  };

  if (!auth) {
    return (
        <div className="flex flex-col items-center justify-center p-12 text-center space-y-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiVideo} className="w-10 h-10 text-blue-600" />
            </div>
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Blink Kameras Verbinden</h1>
                <p className="text-gray-600 mt-2 max-w-md mx-auto">
                    Verbinden Sie Ihren Blink Account, um Live-Status, Batterieanzeigen und Vorschaubilder zu sehen.
                </p>
            </div>
            <button 
                onClick={() => setShowLogin(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-lg"
            >
                Mit Blink verbinden
            </button>
            {showLogin && (
                <BlinkLoginModal 
                    onClose={() => setShowLogin(false)} 
                    onLoginSuccess={handleLoginSuccess} 
                />
            )}
        </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blink Kameras</h1>
          <p className="text-gray-600 mt-2">
            {dashboard?.cameras?.length || 0} Kameras • {auth.region?.toUpperCase() || 'Online'}
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
             <button 
                onClick={fetchDashboard} 
                disabled={refreshing}
                className={`p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 ${refreshing ? 'animate-spin' : ''}`}
            >
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
            </button>
            <button 
                onClick={handleLogout} 
                className="p-2 rounded-lg bg-red-50 border border-red-100 text-red-600 hover:bg-red-100"
                title="Abmelden"
            >
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
            </button>
        </div>
      </div>

      {dashboard && dashboard.cameras ? (
          <div className="space-y-8">
              {/* Group by Network if multiple networks exist (simplified: Just iterate cameras) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dashboard.cameras.map((cam, index) => {
                    const networkId = cam.network_id;
                    const isArmed = getNetworkStatus(networkId);
                    
                    return (
                        <motion.div 
                            key={cam.id} 
                            initial={{ opacity: 0, scale: 0.95 }} 
                            animate={{ opacity: 1, scale: 1 }} 
                            transition={{ delay: index * 0.1 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col"
                        >
                            {/* Camera Preview */}
                            <div className="bg-gray-900 h-48 relative flex items-center justify-center group">
                                {thumbnails[cam.id] ? (
                                    <img src={thumbnails[cam.id]} alt={cam.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                                ) : (
                                    <div className="text-gray-500 text-sm flex flex-col items-center">
                                        <SafeIcon icon={FiVideo} className="w-8 h-8 mb-2" />
                                        Lade Bild...
                                    </div>
                                )}
                                
                                {/* Overlay Status */}
                                <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
                                    <span className="bg-black/60 text-white text-xs px-2 py-1 rounded font-medium backdrop-blur-sm">
                                        {cam.name}
                                    </span>
                                </div>

                                <div className="absolute top-2 right-2">
                                    {isArmed ? (
                                        <span className="bg-green-600 text-white text-xs px-2 py-1 rounded font-bold shadow-sm flex items-center">
                                            <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" /> AKTIV
                                        </span>
                                    ) : (
                                        <span className="bg-gray-500/80 text-white text-xs px-2 py-1 rounded font-medium backdrop-blur-sm">
                                            UNSCHARF
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Camera Details */}
                            <div className="p-4 flex-1 flex flex-col">
                                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                                    <div className="flex items-center" title="Batterie Level">
                                        {/* Battery level is often an integer (e.g. 160 = 1.6V?) or level 1-5. Blink API varies. Assuming level >= 1 */}
                                        <SafeIcon icon={FiBattery} className={`w-4 h-4 mr-1 ${cam.battery_level >= 1 ? 'text-green-500' : 'text-red-500'}`} />
                                        <span className="text-xs">OK</span>
                                    </div>
                                    <div className="flex items-center" title="WLAN Signal">
                                        <SafeIcon icon={FiWifi} className="w-4 h-4 mr-1 text-blue-500" />
                                        <span className="text-xs">{cam.wifi_strength || '-'}</span>
                                    </div>
                                    <div className="flex items-center" title="Temperatur">
                                        <SafeIcon icon={FiThermometer} className="w-4 h-4 mr-1 text-orange-500" />
                                        <span className="text-xs">{cam.temperature ? `${(cam.temperature / 100 * 1.8 + 32).toFixed(0)}°F` : '-'}</span> 
                                        {/* Note: Blink often sends temp in strange formats or F/C depending on region. */}
                                    </div>
                                </div>

                                <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-400">
                                        Netzwerk: {networkId}
                                    </span>
                                    <button
                                        onClick={() => toggleArm(networkId, isArmed ? 'armed' : 'disarmed')}
                                        className={`px-3 py-1 rounded text-xs font-bold uppercase transition-colors ${
                                            isArmed 
                                            ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                                            : 'bg-green-50 text-green-600 hover:bg-green-100'
                                        }`}
                                    >
                                        {isArmed ? 'Deaktivieren' : 'Aktivieren'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
              </div>
          </div>
      ) : (
          <div className="text-center py-12">
              <SafeIcon icon={FiRefreshCw} className="w-10 h-10 text-gray-300 mx-auto animate-spin" />
              <p className="text-gray-500 mt-4">Lade Kameradaten...</p>
          </div>
      )}
    </div>
  );
}